import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { Row, Spinner } from "reactstrap";
import { auth, db } from "../../firebase";
import { FIREBASE_COLLECTIONS } from "../../config/firebaseCollections";
import { fetchUserData, selectUser } from "features/userSlice";
import {
  getCanvaDesign,
  getCanvaPickerSelection,
  resolveCanvaReturn,
} from "utils/canvaUtils";
import { updateContentBox } from "utils/ContentBoxUtils";
import { updateMultiBoxInnerContent } from "utils/multiBoxInnerContentSave";

export default function CanvaReturn() {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const handled = useRef(false);
  const [status, setStatus] = useState("loading");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Canva returns correlation_jwt; design_id + correlation_state are only present
    // if a backend already decoded the JWT (e.g. starter-kit style redirect).
    const correlationJwt = params.get("correlation_jwt");
    const designIdParam = params.get("design_id");
    const correlationStateParam = params.get("correlation_state");

    if (!correlationJwt && (!designIdParam || !correlationStateParam)) {
      setErrorMsg("Canva did not return the information needed to finish editing.");
      setStatus("error");
      return undefined;
    }

    const completeReturn = async (firebaseUser) => {
      if (handled.current) return;
      handled.current = true;

      try {
        const uid = firebaseUser?.uid || user?.uid;
        if (!uid) {
          throw new Error("Could not restore your Lesson Launchpad session.");
        }

        const context = await resolveCanvaReturn(
          correlationJwt
            ? { correlationJwt }
            : {
                designId: designIdParam,
                correlationState: correlationStateParam,
              }
        );

        const designId = context.designId || designIdParam;
        if (!designId) {
          throw new Error("Canva did not return a design id.");
        }

        const designResponse = await getCanvaDesign(designId);
        const selection = getCanvaPickerSelection(designResponse?.design);
        if (!selection) {
          throw new Error("Could not load the updated Canva design.");
        }

        if (context.multiBox && context.parentMultiBoxId) {
          const multiBoxRef = doc(
            db,
            FIREBASE_COLLECTIONS.USERS,
            uid,
            FIREBASE_COLLECTIONS.CLASSES,
            context.classId,
            FIREBASE_COLLECTIONS.CONTENT_BOXES,
            context.parentMultiBoxId
          );
          const multiBoxSnap = await getDoc(multiBoxRef);
          const existingInner = Array.isArray(multiBoxSnap.data()?.content)
            ? multiBoxSnap.data().content.find((box) => box.contentBoxId === context.contentBoxId)
            : null;

          await updateMultiBoxInnerContent({
            userId: uid,
            classId: context.classId,
            multiBoxId: context.parentMultiBoxId,
            updatedBox: {
              ...(existingInner || { contentBoxId: context.contentBoxId, type: "canva" }),
              ...selection,
              contentBoxId: context.contentBoxId,
            },
          });
        } else {
          const contentBoxRef = doc(
            db,
            FIREBASE_COLLECTIONS.USERS,
            uid,
            FIREBASE_COLLECTIONS.CLASSES,
            context.classId,
            FIREBASE_COLLECTIONS.CONTENT_BOXES,
            context.contentBoxId
          );
          const contentBoxSnap = await getDoc(contentBoxRef);
          const existing = contentBoxSnap.exists() ? contentBoxSnap.data() : {};

          const existingSnapOk = await updateContentBox(
            uid,
            context.classId,
            context.contentBoxId,
            {
              ...existing,
              ...selection,
              contentBoxId: context.contentBoxId,
            },
            null
          );
          if (existingSnapOk === false) {
            throw new Error("Could not save the Canva design back to your content box.");
          }
        }

        await dispatch(fetchUserData(uid));
        history.replace(context.returnPath || "/");
      } catch (err) {
        console.error("[CanvaReturn] error:", err);
        handled.current = false;
        setErrorMsg(err.message || "Failed to finish returning from Canva. Please try again.");
        setStatus("error");
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        clearTimeout(authTimeout);
        unsubscribe();
        completeReturn(firebaseUser);
      }
    });

    const authTimeout = setTimeout(() => {
      unsubscribe();
      setErrorMsg("Could not restore your session after returning from Canva. Please sign in and try again.");
      setStatus("error");
    }, 8000);

    return () => {
      clearTimeout(authTimeout);
      unsubscribe();
    };
  }, [dispatch, history, location.search, user?.uid]);

  if (status === "loading") {
    return (
      <Row style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", flexDirection: "column", gap: "16px" }}>
        <Spinner style={{ height: "3rem", width: "3rem" }} color="info" />
        <p style={{ color: "#004aad", fontWeight: "bold" }}>Bringing your Canva design back to Lesson Launchpad...</p>
      </Row>
    );
  }

  return (
    <Row style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", flexDirection: "column", gap: "12px", padding: "20px" }}>
      <i className="fa fa-exclamation-circle" style={{ fontSize: "48px", color: "#e74c3c" }} />
      <h4 style={{ color: "#004aad", fontWeight: "bold" }}>Could Not Finish Canva Edit</h4>
      <p style={{ color: "#666", textAlign: "center", maxWidth: "420px" }}>{errorMsg}</p>
      <button
        onClick={() => history.replace("/")}
        style={{ marginTop: "8px", padding: "10px 24px", borderRadius: "30px", backgroundColor: "#00c4cc", color: "white", border: "none", cursor: "pointer", fontWeight: "bold" }}
      >
        Back to Lesson Launchpad
      </button>
    </Row>
  );
}
